import { EMPTY_NORMALIZED_LTL_DRAFT_ITEM } from "@shared/models/ltlEmptyLine";
import { LTLItem } from "@src/types/components";
import type { Accessorial } from "@src/types/api";
import type { ShipmentDraft } from "@src/load-agent/domain/shipmentDraft";
import type { InferredFormFields } from "@taskpane/utils/computeInferredFormFields";

export type LTLState = ShipmentDraft & {
  errors: Record<string, string>;
  inferredFormFields: InferredFormFields;
};

export type LTLAction =
  | {
      type: "LOAD_FROM_SHIPMENT";
      payload: Partial<LTLState>;
    }
  | {
      type: "SET_FIELD";
      name: keyof LTLState["form"];
      value: string;
    }
  | {
      type: "SET_SHIPMENT_MODE";
      value: "LTL" | "Truckload";
    }
  | {
      type: "SET_EQUIPMENT";
      value: string;
    }
  | {
      type: "SET_ITEM_FIELD";
      index: number;
      field: keyof LTLItem;
      value: unknown;
    }
  | {
      type: "ADD_ITEM";
    }
  | {
      type: "REMOVE_ITEM";
      index: number;
    }
  | {
      type: "TOGGLE_ACCESSORIAL";
      key: string;
    }
  | {
      type: "SET_ERRORS";
      errors: Record<string, string>;
    }
  | {
      type: "RESET";
      payload: Pick<LTLState, "accessorials">;
    };

function applySetItemField(
  state: LTLState,
  action: Extract<LTLAction, { type: "SET_ITEM_FIELD" }>
): LTLState {
  let value: unknown = action.value;

  if (action.field === "weight") {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(/,/g, ""));

    value = Number.isFinite(n) ? n : 0;
  }
  if (action.field === "piecesCount" || action.field === "packagingCount") {
    const n = typeof value === "number" ? value : parseInt(String(value ?? ""), 10);

    value = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  }
  const updatedItems = [...state.items];

  updatedItems[action.index] = {
    ...updatedItems[action.index],
    [action.field]: value,
  };
  const nextErrors = { ...state.errors };

  delete nextErrors[`item${action.index}_${String(action.field)}`];
  const nextInferred = { ...state.inferredFormFields };

  delete nextInferred[`item${action.index}_${String(action.field)}`];
  return {
    ...state,
    items: updatedItems,
    errors: nextErrors,
    inferredFormFields: nextInferred,
  };
}

function applyRemoveItem(
  state: LTLState,
  action: Extract<LTLAction, { type: "REMOVE_ITEM" }>
): LTLState {
  if (state.items.length <= 1) return state;
  const updatedItems = state.items.filter(
    (_: LTLState["items"][number], i: number) => i !== action.index
  );
  const newErrors = { ...state.errors };

  Object.keys(newErrors).forEach((key) => {
    if (key.startsWith(`item${action.index}_`)) {
      delete newErrors[key];
    }
  });
  const nextInferred = { ...state.inferredFormFields };

  for (const k of Object.keys(nextInferred)) {
    if (/^item\d+_/.test(k)) delete nextInferred[k];
  }
  return {
    ...state,
    items: updatedItems,
    errors: newErrors,
    inferredFormFields: nextInferred,
  };
}

export const ltlInitialState: LTLState = {
  form: {
    originZip: "",
    originState: "",
    destinationZip: "",
    destinationState: "",
    pickupDate: "",
    originAddress: "",
    originAddress2: "",
    destinationAddress: "",
    destinationAddress2: "",
    pickupOpenTime: "",
    pickupCloseTime: "",
    shipperName: "",
    consigneeName: "",
    companyName: "",
    shipperContact: "",
    shipperPhone: "",
    consigneeContact: "",
    consigneePhone: "",
    shipperNumber: "",
    poNumber: "",
    billOfLadingNote: "",
  },
  shipmentMode: "LTL",
  equipment: "",
  items: [
    {
      ...EMPTY_NORMALIZED_LTL_DRAFT_ITEM,
      length: undefined,
      width: undefined,
      height: undefined,
    },
  ],
  accessorials: [],
  errors: {},
  inferredFormFields: {},
};

function applyLoadFromShipmentAction(
  state: LTLState,
  action: Extract<LTLAction, { type: "LOAD_FROM_SHIPMENT" }>
): LTLState {
  const payload = action.payload;

  return {
    ...state,
    ...payload,
    form: {
      ...ltlInitialState.form,
      ...(payload.form ?? {}),
    },
    inferredFormFields: payload.inferredFormFields ?? {},
    errors: {},
  };
}

function applySetFormField(
  state: LTLState,
  action: Extract<LTLAction, { type: "SET_FIELD" }>
): LTLState {
  const newForm = {
    ...state.form,
    [action.name]: action.value,
  };
  const nextErrors = { ...state.errors };

  delete nextErrors[String(action.name)];
  const nextInferred = { ...state.inferredFormFields };

  delete nextInferred[action.name];
  return {
    ...state,
    form: newForm,
    errors: nextErrors,
    inferredFormFields: nextInferred,
  };
}

function applySetEquipmentAction(
  state: LTLState,
  action: Extract<LTLAction, { type: "SET_EQUIPMENT" }>
): LTLState {
  const nextErrors = { ...state.errors };

  delete nextErrors.equipment;
  return {
    ...state,
    equipment: action.value,
    errors: nextErrors,
  };
}

export function ltlReducer(state: LTLState, action: LTLAction): LTLState {
  switch (action.type) {
    case "LOAD_FROM_SHIPMENT":
      return applyLoadFromShipmentAction(state, action);
    case "SET_FIELD":
      return applySetFormField(state, action);
    case "SET_SHIPMENT_MODE":
      return {
        ...state,
        shipmentMode: action.value,
      };
    case "SET_EQUIPMENT":
      return applySetEquipmentAction(state, action);
    case "SET_ITEM_FIELD":
      return applySetItemField(state, action);
    case "ADD_ITEM":
      return {
        ...state,
        items: [
          ...state.items,
          {
            ...EMPTY_NORMALIZED_LTL_DRAFT_ITEM,
            length: undefined,
            width: undefined,
            height: undefined,
          },
        ],
      };
    case "REMOVE_ITEM":
      return applyRemoveItem(state, action);
    case "TOGGLE_ACCESSORIAL":
      return {
        ...state,
        accessorials: state.accessorials.map((acc: LTLState["accessorials"][number]) =>
          acc.key === action.key ? { ...acc, checked: !acc.checked } : acc
        ),
      };
    case "SET_ERRORS":
      return {
        ...state,
        errors: action.errors,
      };
    case "RESET":
      return {
        ...ltlInitialState,
        accessorials: action.payload.accessorials,
      };
    default:
      return state;
  }
}

/** @internal hook wiring resets accessorials from org defaults */
export function ltlReducerStateWithAccessorials(defaultAccessorials: Accessorial[]): LTLState {
  return {
    ...ltlInitialState,
    accessorials: defaultAccessorials,
  };
}
